import type { FC } from "react";

import noop from "lodash/noop";

import {
    PixelPlaceholderLoader,
    Checkbox,
    Button,
    ColumnsPickerLiteWithDragAndDrop,
    OnOffSwitch,
    FlexRow,
    Tooltip,
} from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import {
    ButtonsWrapper,
    ButtonWrapper,
    WidgetHeaderContainer,
    WidgetHeaderSwitcherContainer,
    WidgetMetadataContainer,
    WidgetActionsContainer,
    WidgetTitle,
    WidgetActionsTitle,
    WidgetActionsItemWrapper,
    WidgetOnOffWrapper,
    CloseButton,
} from "app/components/React/WidgetContainer/styledComponents";
import type { IColumnPickerConfig, IWidgetHeaderProps } from "app/components/React/WidgetContainer/types";
import { WeShare } from "app/components/weshare/weshare";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

const ColumnPickerNode = ({ columnPickerConfig }: { columnPickerConfig: IColumnPickerConfig }) => {
    const t = useTranslation();

    if (!columnPickerConfig) {
        return null;
    }

    const { tooltipKey, isEnabled, tooltipText, dropdownKey, ...restColumnPickerConfig } = columnPickerConfig;

    const tooltipProps = {
        key: tooltipKey,
        enabled: isEnabled,
        text: tooltipText,
    };

    return (
        <PlainTooltip {...tooltipProps}>
            <WidgetActionsItemWrapper key={dropdownKey}>
                <ColumnsPickerLiteWithDragAndDrop
                    {...restColumnPickerConfig}
                    translations={{
                        tooltip: t("workspace.sales.column.picker"),
                        getSelectedAmountText: ({ selectedValue, totalValue }) =>
                            t("common.columnsPicker.selectedAmount", {
                                selected: selectedValue,
                                total: totalValue,
                            }),
                        mandatoryField: t("common.columnsPicker.mandatoryField"),
                        apply: t("common.apply"),
                    }}
                />
            </WidgetActionsItemWrapper>
        </PlainTooltip>
    );
};

type GetDownloadBtnParams = {
    downloadUrl?: string;
    isExcel: boolean;
    onClickCallback?: any;
    excelLocked?: boolean;
    requestBodyForExcel?: object;
};

export const WidgetHeader: FC<IWidgetHeaderProps> = ({
    title = "",
    actionsTitle = "",
    headerHeight,
    tooltip,
    subtitleFilters,
    isLoading = false,
    withBorder,
    excelUrl,
    onExcelClick,
    isExcelDisabled,
    hideExcelButton,
    onDownloadClick,
    downloadProps = {},
    onOffSwitchConfig,
    checkboxConfig,
    dynamicButtonsConfig,
    switcherConfig,
    switcherViewMode,
    dynamicSwitcherConfig,
    dynamicDropdownConfig,
    columnPickerConfig,
    addToDashboardConfig,
    weShareProps,
    noData,
    headerBadge,
    headerTitleSuffix,
    requestBodyForExcel,
    totalCount,
    descriptionText,
    hasPaidExcel,
    closeButtonProps,
}) => {
    const downloadExcelPermitted = !!swSettings?.current?.resources?.IsExcelAllowed;
    const isPaidExcelPermitted = !!isPaidExcelAllowed();

    const getDownloadBtn = ({
        downloadUrl,
        isExcel,
        onClickCallback,
        excelLocked = false,
        requestBodyForExcel: requestBodyForExcelParam,
    }: GetDownloadBtnParams) => {
        const onClickCallbackProp = isExcel
            ? { exportFunction: onClickCallback }
            : onClickCallback
              ? { handleDownloadFunction: onClickCallback }
              : {};
        return (isExcel && downloadUrl && !!onClickCallback) || (!isExcel && !!onClickCallback) ? (
            <ButtonWrapper>
                <DownloadButtonMenu
                    Excel={isExcel}
                    // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'boolean | undefined' — component expects undefined but code uses null
                    PaidExcel={hasPaidExcel ? isPaidExcelPermitted : null}
                    totalCount={totalCount}
                    PNG={!isExcel}
                    downloadUrl={downloadExcelPermitted ? downloadUrl : ""}
                    excelLocked={isExcel ? excelLocked || !downloadExcelPermitted : false}
                    excelBodyForReq={requestBodyForExcelParam}
                    {...onClickCallbackProp}
                    {...downloadProps}
                />
            </ButtonWrapper>
        ) : null;
    };

    const hasSubtitleFilters = Array.isArray(subtitleFilters) && subtitleFilters.length > 0;
    const hasActionButtons =
        !noData &&
        !!(
            weShareProps ||
            addToDashboardConfig ||
            onDownloadClick ||
            (!hideExcelButton && excelUrl && onExcelClick) ||
            (columnPickerConfig && !columnPickerConfig.extendedDropdown)
        );

    return (
        <WidgetHeaderContainer
            withBorder={withBorder}
            hasSubtitleFilters={hasSubtitleFilters}
            hasDescriptionText={!!descriptionText}
            $headerHeight={headerHeight}
        >
            <FlexRow>
                <WidgetMetadataContainer hideSubtitles={!hasSubtitleFilters}>
                    <WidgetTitle hideSubtitles={!hasSubtitleFilters}>
                        <BoxTitle
                            tooltip={tooltip}
                            badge={headerBadge}
                            customElement={headerTitleSuffix}
                        >
                            {title}
                        </BoxTitle>
                    </WidgetTitle>
                    {hasSubtitleFilters ? (
                        isLoading ? (
                            <PixelPlaceholderLoader
                                width={304}
                                height={24}
                            />
                        ) : (
                            <StyledBoxSubtitle>
                                <BoxSubtitle filters={subtitleFilters} />
                            </StyledBoxSubtitle>
                        )
                    ) : null}
                </WidgetMetadataContainer>
                <WidgetActionsContainer>
                    {actionsTitle ? <WidgetActionsTitle>{actionsTitle}</WidgetActionsTitle> : null}
                    {onOffSwitchConfig?.length && onOffSwitchConfig.length > 0
                        ? onOffSwitchConfig.map((switchConfig, index) => {
                              const onOffSwitchElement = (
                                  <WidgetOnOffWrapper key={`OnOffSwitch-${index}`}>
                                      <OnOffSwitch
                                          className={switchConfig.className}
                                          isSelected={switchConfig.isSelected}
                                          onClick={switchConfig.onClick}
                                          isDisabled={switchConfig.isDisabled}
                                          offLabel={switchConfig.offLabel}
                                          onLabel={switchConfig.onLabel}
                                      />
                                  </WidgetOnOffWrapper>
                              );

                              if (switchConfig.tooltip) {
                                  return (
                                      <Tooltip
                                          key={`OnOffSwitchTooltip-${index}`}
                                          {...switchConfig.tooltip}
                                      >
                                          {onOffSwitchElement}
                                      </Tooltip>
                                  );
                              }

                              return onOffSwitchElement;
                          })
                        : null}

                    {checkboxConfig?.length && checkboxConfig.length > 0 ? (
                        <WidgetActionsItemWrapper>
                            {checkboxConfig.map((checkboxConfig, index) => (
                                <Checkbox
                                    key={`checkbox-${index}`}
                                    label={checkboxConfig.label}
                                    onClick={checkboxConfig.onClick}
                                    isDisabled={checkboxConfig.isDisabled}
                                    selected={checkboxConfig.selected}
                                    halfSelected={checkboxConfig.halfSelected}
                                    className={checkboxConfig.className}
                                    color={checkboxConfig.color}
                                />
                            ))}
                        </WidgetActionsItemWrapper>
                    ) : null}
                    {dynamicButtonsConfig?.length && dynamicButtonsConfig.length > 0 ? (
                        <WidgetActionsItemWrapper>
                            {dynamicButtonsConfig.map((dynamicButtonConfig, index) => (
                                <Button
                                    key={`dynamicButtons-${index}`}
                                    onClick={dynamicButtonConfig.onClick}
                                    isLoading={dynamicButtonConfig.isLoading}
                                    type={dynamicButtonConfig.type}
                                    isDisabled={dynamicButtonConfig.isDisabled}
                                    isActive={dynamicButtonConfig.isActive}
                                    isHover={dynamicButtonConfig.isHover}
                                    isActivated={dynamicButtonConfig.isActivated}
                                >
                                    {dynamicButtonConfig.label}
                                </Button>
                            ))}
                        </WidgetActionsItemWrapper>
                    ) : null}
                    {switcherViewMode ? (
                        <WidgetHeaderSwitcherContainer>
                            <SwitcherGranularityContainer
                                itemList={switcherViewMode.items}
                                selectedIndex={switcherViewMode.initialSelectedItem}
                                // @ts-ignore FIXME: strict migration - Type '((itemId: any) => void) | undefined' is not assignable to type '(newIndex: string | number) => void' — ISwitcherConfig and ISwitcherGranularityContainer have incompatible onItemClick signatures
                                onItemClick={switcherViewMode.onItemClick}
                                customClass="CircleSwitcher"
                            />
                        </WidgetHeaderSwitcherContainer>
                    ) : null}

                    {switcherConfig ? (
                        <WidgetHeaderSwitcherContainer>
                            <SwitcherGranularityContainer
                                itemList={switcherConfig.items}
                                selectedIndex={switcherConfig.initialSelectedItem}
                                // @ts-ignore FIXME: strict migration - Type '((itemId: any) => void) | undefined' is not assignable to type '(newIndex: string | number) => void' — ISwitcherConfig and ISwitcherGranularityContainer have incompatible onItemClick signatures
                                onItemClick={switcherConfig.onItemClick}
                                customClass="CircleSwitcher"
                            />
                        </WidgetHeaderSwitcherContainer>
                    ) : null}

                    {dynamicSwitcherConfig?.length && dynamicSwitcherConfig.length > 0 ? (
                        <WidgetActionsItemWrapper>
                            {dynamicSwitcherConfig.map((dynamicSwitcher, index) => (
                                <SwitcherGranularityContainer
                                    key={`dynamicSwitcher-${index}`}
                                    itemList={dynamicSwitcher.items}
                                    selectedIndex={dynamicSwitcher.initialSelectedItem}
                                    // @ts-ignore FIXME: strict migration - Type '((itemId: any) => void) | undefined' is not assignable to type '(newIndex: string | number) => void' — ISwitcherConfig and ISwitcherGranularityContainer have incompatible onItemClick signatures
                                    onItemClick={dynamicSwitcher.onItemClick}
                                    customClass="CircleSwitcher"
                                    className="swichers-container"
                                />
                            ))}
                        </WidgetActionsItemWrapper>
                    ) : null}

                    {dynamicDropdownConfig?.length && dynamicDropdownConfig.length > 0
                        ? dynamicDropdownConfig.map((dropdownConfig, index) => {
                              const dropdownId = `widget-dropdown-${dropdownConfig.key ?? index}`;
                              return (
                                  <WidgetActionsItemWrapper
                                      key={dropdownConfig.key ?? `dynamicDropdown-${index}`}
                                      width={dropdownConfig.width}
                                      className={dropdownConfig?.className}
                                      id={dropdownId}
                                  >
                                      <StatefulDropdown
                                          items={dropdownConfig.items}
                                          selectedId={dropdownConfig.selectedId}
                                          onSelect={dropdownConfig.onSelect}
                                          disabled={dropdownConfig.disabled}
                                          title={dropdownConfig.title}
                                          DropDownButtonComponent={dropdownConfig.DropDownButtonComponent}
                                          DropDownItemComponent={dropdownConfig.DropDownItemComponent}
                                          width={dropdownConfig.width}
                                          onToggle={dropdownConfig.onToggle ?? noop}
                                          appendTo={`#${dropdownId}`}
                                      />
                                  </WidgetActionsItemWrapper>
                              );
                          })
                        : null}

                    {!!columnPickerConfig && columnPickerConfig.extendedDropdown ? (
                        <ColumnPickerNode columnPickerConfig={columnPickerConfig} />
                    ) : null}

                    {hasActionButtons ? (
                        <ButtonsWrapper>
                            {!!columnPickerConfig && !columnPickerConfig.extendedDropdown ? (
                                <ColumnPickerNode columnPickerConfig={columnPickerConfig} />
                            ) : null}
                            {!hideExcelButton
                                ? getDownloadBtn({
                                      downloadUrl: excelUrl,
                                      isExcel: true,
                                      onClickCallback: onExcelClick,
                                      excelLocked: isExcelDisabled,
                                      requestBodyForExcel,
                                  })
                                : null}
                            {getDownloadBtn({
                                // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined' — component accepts null for no download
                                downloadUrl: null,
                                isExcel: false,
                                onClickCallback: onDownloadClick,
                                requestBodyForExcel,
                            })}
                            {addToDashboardConfig ? (
                                <ButtonWrapper>
                                    <AddToDashboard
                                        webSource={addToDashboardConfig.webSource}
                                        type={addToDashboardConfig.type}
                                        metric={addToDashboardConfig.metric}
                                        filters={addToDashboardConfig.filters}
                                        width={addToDashboardConfig.widgetWidth}
                                        modelType={addToDashboardConfig.modelType ?? "fromMobile"}
                                        overrideAddToDashboardParams={addToDashboardConfig.overrideAddToDashboardParams}
                                        keys={addToDashboardConfig.keys}
                                        currentCampaignConfiguration={addToDashboardConfig.currentCampaignConfiguration}
                                        country={addToDashboardConfig.country}
                                        duration={addToDashboardConfig.duration}
                                        selectedSite={addToDashboardConfig.selectedSite}
                                        customAsset={addToDashboardConfig.customAsset}
                                        buttonProps={addToDashboardConfig.buttonProps}
                                    />
                                </ButtonWrapper>
                            ) : null}
                            {weShareProps ? (
                                <ButtonWrapper>
                                    <WeShare
                                        chartRef={weShareProps.onWeShareClick}
                                        description={title}
                                        title={title}
                                        redirectUrl={weShareProps.getRedirectUrl?.()}
                                        isHighChart={weShareProps.isHighChart}
                                    />
                                </ButtonWrapper>
                            ) : null}
                        </ButtonsWrapper>
                    ) : null}
                </WidgetActionsContainer>
                {closeButtonProps?.isDisplayed ? (
                    <CloseButton
                        iconSize="xs"
                        type={buttonTypes.FLAT}
                        iconName="close"
                        onClick={() => closeButtonProps.onClick()}
                    />
                ) : null}
            </FlexRow>
            {descriptionText ? <div>{descriptionText}</div> : null}
        </WidgetHeaderContainer>
    );
};
